import { openPopup } from '@gobistories/gobi-web-integration';
import type { Options } from '@gobistories/gobi-web-integration/dist/types/options.types';
import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { ActivityIndicator } from '~/Partials/ActivityIndicator/ActivityIndicator';
import { useFetchData } from '~/Partials/FetchData/useFetchData';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { PlayButton } from '~/Partials/PlayButton/PlayButton';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import type { Theme } from '~/Utils/Theme';
import styles from './GobiStory.module.css';
import type { GobiApiResponseNarrowed } from './Utils/GobiApiUtils';
import { baseApiUrl } from './Utils/GobiApiUtils';

export interface GobiStoryProps {
    storyId: string;
    openLabel: string;
    errorMessage: string;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    className?: string;
}

export const GobiStory: React.FC<GobiStoryProps> = props => {
    const contextualTheme = useTheme();
    const { storyId, openLabel, errorMessage, className, theme = contextualTheme } = props;

    const [isOpened, setIsOpened] = React.useState(false);
    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLButtonElement>();

    const { withRoundedCorners } = theme;

    const openStory = () => {
        if (isOpened) {
            return;
        }

        setIsOpened(true);

        openPopup(storyId, {
            on: {
                close: () => setIsOpened(false),
            },
        } as Options);
    };

    const { data, loading, error } = useFetchData<GobiApiResponseNarrowed>({
        url: `${baseApiUrl}/${storyId}`,
    });

    const renderContent = () => {
        if (error) {
            return (
                <div className={styles.ErrorContainer}>
                    <SvgIcon iconName="video-off" className={styles.ErrorIcon} />
                    <span className={styles.ErrorMessage}>{errorMessage}</span>
                </div>
            );
        }

        if (loading || !data) {
            return <ActivityIndicator active cover />;
        }

        const { posterUrl, title } = data;

        return (
            <>
                <Picture profile={PictureProfiles.vertical} url={posterUrl} alt={title} />
                <div>
                    <div
                        className={styles.PlayButtonWrapper}
                        onMouseDown={onProxyMouseDown}
                        onMouseUp={onProxyMouseUp}
                        role="none"
                    >
                        <PlayButton
                            onClick={openStory}
                            buttonRef={clickableRef}
                            label={openLabel}
                            className={styles.PlayButton}
                        />
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={classNames(styles.GobiStory, withRoundedCorners && styles['Variant--rounded'], className)}>
            {renderContent()}
        </div>
    );
};
